import { useState, useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, Step, ACTIONS, EVENTS } from 'react-joyride';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/components/AuthProvider';

const ONBOARDING_STORAGE_KEY = 'obrim-onboarding-completed';

// Tour steps configuration
const tourSteps: Step[] = [
    {
        target: 'body',
        content: (
            <div className="text-center">
                <h3 className="text-lg font-bold mb-2">🎉 Bem-vindo ao Obrim!</h3>
                <p className="text-sm text-muted-foreground">
                    Vamos fazer um tour rápido para você conhecer as principais funcionalidades.
                </p>
            </div>
        ),
        placement: 'center',
        disableBeacon: true,
    },
    {
        target: '[data-tour="dashboard-hero"]',
        content: (
            <div>
                <h4 className="font-semibold mb-1">📊 Visão Geral</h4>
                <p className="text-sm text-muted-foreground">
                    Aqui você vê o resumo financeiro do mês, projetos ativos e alertas importantes.
                </p>
            </div>
        ),
        placement: 'bottom',
    },
    {
        target: '[data-tour="quick-actions"]',
        content: (
            <div>
                <h4 className="font-semibold mb-1">⚡ Ações Rápidas</h4>
                <p className="text-sm text-muted-foreground">
                    Crie projetos, cadastre clientes ou gere propostas com um clique.
                </p>
            </div>
        ),
        placement: 'top',
    },
    {
        target: '[data-tour="ai-widgets"]',
        content: (
            <div>
                <h4 className="font-semibold mb-1">🤖 Insights de IA</h4>
                <p className="text-sm text-muted-foreground">
                    A IA analisa seus dados e mostra a saúde do escritório e previsões de atrasos.
                    Clique para acessar todas as ferramentas de IA.
                </p>
            </div>
        ),
        placement: 'top',
    },
    {
        target: '[data-tour="fab-button"]',
        content: (
            <div>
                <h4 className="font-semibold mb-1">➕ Menu de Ações</h4>
                <p className="text-sm text-muted-foreground">
                    Toque aqui para criar diário de obra por voz, registrar despesas ou conversar com a IA.
                </p>
            </div>
        ),
        placement: 'top',
    },
    {
        target: '[data-tour="projects-carousel"]',
        content: (
            <div>
                <h4 className="font-semibold mb-1">🏗️ Seus Projetos</h4>
                <p className="text-sm text-muted-foreground">
                    Navegue pelos seus projetos recentes e acesse detalhes rapidamente.
                </p>
            </div>
        ),
        placement: 'top',
    },
    {
        target: 'body',
        content: (
            <div className="text-center">
                <h3 className="text-lg font-bold mb-2">✅ Pronto para começar!</h3>
                <p className="text-sm text-muted-foreground mb-2">
                    Explore o Obrim e descubra como gerenciar suas obras de forma inteligente.
                </p>
                <p className="text-xs text-primary">
                    💡 Dica: Use a Central de IA (/ia) para acessar todas as ferramentas inteligentes!
                </p>
            </div>
        ),
        placement: 'center',
    },
];

interface OnboardingTourProps {
    forceRun?: boolean;
    onComplete?: () => void;
}

export function OnboardingTour({ forceRun = false, onComplete }: OnboardingTourProps) {
    const [run, setRun] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        // Only run on dashboard
        if (location.pathname !== '/dashboard') {
            setRun(false);
            return;
        }

        // Check if user has completed onboarding
        if (forceRun) {
            setRun(true);
            return;
        }

        const hasCompleted = localStorage.getItem(ONBOARDING_STORAGE_KEY);
        if (!hasCompleted && user) {
            // Small delay to let the page render
            const timer = setTimeout(() => {
                setRun(true);
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [location.pathname, user, forceRun]);

    const handleJoyrideCallback = (data: CallBackProps) => {
        const { status, action, index, type } = data;

        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status as any)) {
            setRun(false);
            localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
            onComplete?.();
        }

        if (type === EVENTS.STEP_AFTER) {
            setStepIndex(index + 1);
        }

        if (action === ACTIONS.CLOSE) {
            setRun(false);
            localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
        }
    };

    return (
        <Joyride
            steps={tourSteps}
            run={run}
            stepIndex={stepIndex}
            continuous
            showProgress
            showSkipButton
            scrollToFirstStep
            spotlightClicks
            disableOverlayClose
            callback={handleJoyrideCallback}
            locale={{
                back: 'Voltar',
                close: 'Fechar',
                last: 'Finalizar',
                next: 'Próximo',
                skip: 'Pular',
            }}
            styles={{
                options: {
                    primaryColor: 'hsl(var(--primary))',
                    textColor: 'hsl(var(--foreground))',
                    backgroundColor: 'hsl(var(--card))',
                    overlayColor: 'rgba(0, 0, 0, 0.5)',
                    arrowColor: 'hsl(var(--card))',
                    zIndex: 10000,
                },
                tooltip: {
                    borderRadius: 12,
                    padding: 16,
                },
                buttonNext: {
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontSize: 14,
                },
                buttonBack: {
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontSize: 14,
                    marginRight: 8,
                },
                buttonSkip: {
                    fontSize: 12,
                    color: 'hsl(var(--muted-foreground))',
                },
                spotlight: {
                    borderRadius: 12,
                },
            }}
            floaterProps={{
                disableAnimation: true,
            }}
        />
    );
}

// Hook to restart tour
export function useOnboardingTour() {
    const [shouldRun, setShouldRun] = useState(false);

    const restartTour = () => {
        localStorage.removeItem(ONBOARDING_STORAGE_KEY);
        setShouldRun(true);
    };

    const hasCompletedTour = () => {
        return localStorage.getItem(ONBOARDING_STORAGE_KEY) === 'true';
    };

    return { shouldRun, restartTour, hasCompletedTour };
}
